import React, { useState, useRef, useCallback } from 'react'
import cn from 'classnames';
import { Query } from 'react-apollo';
import GridFlaskController from '../../controllers/GridFlaskController';
import GridFlask from '../../components/GridFlask';
import PageContainer from '../../components/PageContainer';
import ActionsHeader from '../../components/ActionsHeader';
import BlockingStatus from '../../components/BlockingStatus';
import Col from '../../components/Col';
import { REP_OPTIONS as RepFilterOptions } from '../../components/actions/RepFilterAction';
import ActionsOrganizer from './components/ActionsOrganizerSales';
import userSettingsQuery from '../../graphql/queries/userSettings.query';
import { getDefaultUserSettings, getUserSettings } from '../../utils'
import formatters from '../invoices/formatters'
import { isAccountRep } from '../../utils/isAccountRep';
import ArrayUtils from '../../utils/arrayUtils'
import { MobileView, useMobileMediaQuery } from '../../config/mediaQuery';
import ModuleMenuBar from '../../components/ModuleMenuBar';
import useMenuHandler from '../../hooks/useMenuHandler';

type Props = {
  isFullPage: boolean,
  showGroupPanel?: boolean,
  active: boolean,
};

const SalesAnalysis = (props: Props) => {
  const moduleName = 'sales_analysis'

  const {
    isFullPage = true,
    showGroupPanel = false,
    active
  } = props;

  const menuRef = useRef();
  const menuButtonRef = useRef();

  const isMobile = useMobileMediaQuery();
  const [displayMenu, setDisplayMenu] = useState(false);

  // State to track the displayed view name for UI updates
  // - undefined: initial state, will sync from query on first load
  // - null: user selected "Default View"
  // - string: user selected a named view
  const [displayedViewName, setDisplayedViewName] = useState(undefined);

  const changeDisplayMenu = function(status) {
    setDisplayMenu(status);
  };

  const handler = useCallback(function(e) {
    if (menuRef.current
      && menuButtonRef.current
      && !menuRef.current.contains(e.target)
      && !menuButtonRef.current.contains(e.target)
    ) {
      setDisplayMenu(function(value) {
        if (value) {
          return false;
        }
        return value;
      });
    }
  }, []);

  useMenuHandler(handler, active);

  /**
   * Safely coerce a value to a finite number, treating null/undefined/NaN as 0.
   * Wijmo's getAggregate() can return null or NaN when groups have no valid
   * numeric data for a binding, and data row fields can also be null/undefined.
   */
  const toNum = (val) => {
    const n = Number(val)
    return isFinite(n) ? n : 0
  }

  /**
   * Compute percentage difference with edge case handling:
   *   - LY = 0, Current > 0 (i.e. diff != 0): display "N/A" (new sales, no baseline)
   *   - LY = 0, Current = 0 (i.e. diff == 0): display "0.00%"
   *   - LY > 0, Current = 0: display -100% (calculable)
   *   - Normal case: (diff / ly) * 100
   *
   * Inputs are coerced via toNum() so null/undefined/NaN from
   * getAggregate() or data fields are safely handled.
   *
   * Returns { text: string, negative: boolean } for the cell.
   */
  const computePctDiff = (rawDiff, rawLy, format, wjcCore) => {
    const diff = toNum(rawDiff)
    const ly = toNum(rawLy)

    if (ly === 0) {
      if (diff !== 0) {
        // Previous = 0, Current > 0 → no baseline to compare against
        return { text: 'N/A', negative: false }
      } else {
        // Previous = 0, Current = 0 → no change
        return { text: '0.00%', negative: false }
      }
    }
    // Normal calculation
    const pct = (diff / ly) * 100
    const text = format
      ? wjcCore.Globalize.format(pct, format)
      : wjcCore.Globalize.format(pct, 'n2') + '%'
    return { text, negative: pct < 0 }
  }

  const salesPercentAggregation = (grid, wjGrid, wjcCore) => {
    return grid.formatItem.addHandler((s, e) => {
      // cells (both data rows and group rows)
      if (e.panel === s.cells) {
        // get row, column, and data item (or group description)
        let r = s.rows[e.row]
        let c = s.columns[e.col]
        let item = s.rows[e.row].dataItem
        let group = r instanceof wjGrid.GroupRow ? item : null
        let negative = false

        if (c.binding === 'MTD_PCT') {
          if (group) {
            // Group row: aggregate sums across child items
            let mtdDiff = group.getAggregate('Sum', 'MTD_DIFF')
            let mtdLy = group.getAggregate('Sum', 'MTD_LY')
            let result = computePctDiff(mtdDiff, mtdLy, c.format, wjcCore)
            e.cell.textContent = result.text
            negative = result.negative
          } else {
            // Data row: check underlying LY value for edge cases
            let result = computePctDiff(item['MTD_DIFF'], item['MTD_LY'], c.format, wjcCore)
            e.cell.textContent = result.text
            negative = result.negative
          }
        }

        if (c.binding === 'YTD_PCT') {
          if (group) {
            // Group row: aggregate sums across child items
            let ytdDiff = group.getAggregate('Sum', 'YTD_DIFF')
            let ytdLy = group.getAggregate('Sum', 'YTD_LY')
            let result = computePctDiff(ytdDiff, ytdLy, c.format, wjcCore)
            e.cell.textContent = result.text
            negative = result.negative
          } else {
            // Data row: check underlying LY value for edge cases
            let result = computePctDiff(item['YTD_DIFF'], item['YTD_LY'], c.format, wjcCore)
            e.cell.textContent = result.text
            negative = result.negative
          }
        }

        // update 'negative' class on cell
        wjcCore.toggleClass(e.cell, 'negative', negative)
      }

      // footer
      if (e.panel === s.columnFooters) {
        let c = s.columns[e.col]
        let negative = false

        if (c.binding === 'MTD_PCT') {
          let mtdDiff = 0
          let mtdLy = 0
          for (let val of s.collectionView.items) {
            mtdDiff += toNum(val['MTD_DIFF']);
            mtdLy += toNum(val['MTD_LY']);
          }
          let result = computePctDiff(mtdDiff, mtdLy, c.format, wjcCore)
          e.cell.textContent = result.text
          negative = result.negative
        }

        if (c.binding === 'YTD_PCT') {
          let ytdDiff = 0
          let ytdLy = 0
          for (let val of s.collectionView.items) {
            ytdDiff += toNum(val['YTD_DIFF']);
            ytdLy += toNum(val['YTD_LY']);
          }
          let result = computePctDiff(ytdDiff, ytdLy, c.format, wjcCore)
          e.cell.textContent = result.text
          negative = result.negative
        }

        // update 'negative' class on cell
        wjcCore.toggleClass(e.cell, 'negative', negative)
      }
    })
  }

  return (
    <PageContainer
      className={cn({
        'page--is-full-page': isFullPage,
        'page--is-nested': !isFullPage,
        'page--with-group-panel': showGroupPanel,
      })}
      isFullPage={isFullPage}
      active={props.active}
    >
      <Query
        query={userSettingsQuery}
        variables={{
          moduleName: moduleName,
        }}
      >
        {({ data: dataGrid, loading }: any) => {
          // we need to wait until the Query is loaded otherwise
          // we'll have to refetch the grid multiple times (first on
          // page load and then when this query is loaded)
          if (loading || !dataGrid)
            return (
              <BlockingStatus
                excludeHeader
                active
                container={document.body}
                text="Loading..."
              />
            );

          const userSettings = getUserSettings(dataGrid, {
            defaultFilters: function(settings) {
              if (settings && isAccountRep(settings.accountType)) {
                var defaultOption = RepFilterOptions[0];

                return {
                  Rep: {
                    // This could look to something like this:
                    //   Rep: {
                    //     AcctR: {
                    //       eq: 'CO01',
                    //     },
                    //   },
                    [defaultOption.value]: {
                      eq: settings.repCode,
                    },
                  },
                };
              }

              return {};
            },
          });

          const defaultUserSettings = getDefaultUserSettings(dataGrid)

          // Get view name from query for initial sync
          var queryViewName = dataGrid && dataGrid.userSettings && dataGrid.userSettings.viewName
            ? dataGrid.userSettings.viewName
            : null;

          // Only sync displayedViewName from query on initial load (when displayedViewName is undefined)
          // After initial load, displayedViewName is managed entirely by user selection
          if (displayedViewName === undefined) {
            // Use setTimeout to avoid setState during render
            setTimeout(function() { setDisplayedViewName(queryViewName); }, 0);
          }

          // Determine what to show in the UI:
          // - If displayedViewName is undefined (initial load), use queryViewName
          // - Otherwise use displayedViewName (which could be null for Default View)
          var currentViewNameForUI = displayedViewName === undefined ? queryViewName : displayedViewName;

          // Handler for when a view is loaded - just updates the UI display name
          // The actual view settings are applied by onApplyViewSettings in ActionsHeader
          var handleViewLoaded = function(viewName) {
            setDisplayedViewName(viewName);
          };

          return (
            <GridFlaskController
              entity={moduleName}
              flattenFunction={ArrayUtils.noFlatteningCollection}
              loading={loading}
              defaultSorting={defaultUserSettings.defaultSorting}
              defaultColumns={defaultUserSettings.defaultColumns}
              defaultGroupPanels={defaultUserSettings.defaultGroupPanels}
              isMobile={isMobile}
              {...userSettings}
            >
              {({
                collection,
                columnsMapping,
                currentItem,
                defaultColumns,
                frozenColumns,
                grid,
                groups,
                hasAllColumnsHidden,
                onRowSelected,
                onGridInitialized,
                onGroupPanelChanged,
                onColSorted,
                onColResized,
                onColDragged,
                onColFreezed,
                onRowFreezed,
                onGroupCollapsedChanged,
                onClearSelection,
                onColumnsMappingChange,
                onCurrentItemChanged,
                onViewSaved,
                onInternalGridFiltersChanged,
                onViewReset,
                onApplyViewSettings,
                onExporting,
                onExported,
                hasDataToDisplay,
                refetch,
                isLoading,
                isCollapseLoading,
                setCollapseLoading,
                status,
                saveStatus,
                selectedItems,
                userViewState,
                playResetAnimation,
                handleResetAnimation,
                setFlexGridFilter,
                clearInternalFilters,
                toggleMultiSort,
                multiColumnSort,
                exportProgress,
                setExportProgress,
                getGridFilterDefinition,
              }) => {
                return (
                  <React.Fragment>
                    {/* Show loading indicator during collapse/expand operations */}
                    {isCollapseLoading && (
                      <BlockingStatus
                        excludeHeader
                        active
                        container={document.body}
                        text="Processing..."
                      />
                    )}
                    <ActionsHeader
                      grid={grid}
                      collection={collection}
                      defaultColumnsMapping={defaultColumns}
                      columnsMapping={columnsMapping}
                      selectedItems={selectedItems}
                      moduleName={moduleName}
                      groups={groups}
                      saveStatus={saveStatus}
                      status={status}
                      loading={isLoading || status !== 'loaded'}
                      onViewSaved={onViewSaved}
                      onViewReset={onViewReset}
                      onApplyViewSettings={onApplyViewSettings}
                      onColumnsMappingChange={onColumnsMappingChange}
                      onExporting={onExporting}
                      onExported={onExported}
                      userViewState={userViewState}
                      refetch={refetch}
                      hasAllColumnsHidden={hasAllColumnsHidden}
                      hasDataToDisplay={hasDataToDisplay}
                      frozenColumns={frozenColumns}
                      currentItem={currentItem}
                      defaultSettings={defaultUserSettings}
                      handleResetAnimation={handleResetAnimation}
                      toggleMultiSort={toggleMultiSort}
                      multiColumnSort={multiColumnSort}
                      setExportProgress={setExportProgress}
                      // Multiple saved views feature props
                      useMultipleViews={true}
                      currentViewName={currentViewNameForUI}
                      onViewLoaded={handleViewLoaded}
                      getGridFilterDefinition={getGridFilterDefinition}
                    >
                      {function(actions) {
                        var isOneRowSelected = actions.isOneRowSelected;
                        var ColumnsAction = actions.ColumnsAction;
                        return (
                          <>
                            <MobileView>
                              <ModuleMenuBar
                                {...actions}
                                moduleRefetch={refetch}
                                ColumnsAction={ColumnsAction}
                                changeDisplayMenu={changeDisplayMenu}
                                displayMenu={displayMenu}
                                showCollapse={true}
                                onGroupCollapsedChanged={onGroupCollapsedChanged}
                                menuButtonRef={menuButtonRef}
                                clearInternalFilters={clearInternalFilters}
                              />
                            </MobileView>
                            <div
                              ref={menuRef}
                              style={{
                                justifyContent: 'space-between',
                                padding: '0 10px',
                                display: 'flex',
                                flexDirection: 'row',
                                minHeight: 'auto',
                                ...(isMobile && {
                                  position: 'absolute',
                                  bottom: 0,
                                  boxShadow:
                                    '0px -4px 4px 0px #ACACAC40',
                                  zIndex: 100,
                                  background: '#fff',
                                  borderRadius: '8px 8px 0px 0px',
                                  width: '100%',
                                  display: isOneRowSelected || displayMenu
                                    ? 'flex'
                                    : 'none',
                                  justifyContent: 'center',
                                  height: (displayMenu && '189px') || (isOneRowSelected && '150px')
                                }),
                              }}
                            >
                              <Col>
                                <ActionsOrganizer
                                  {...actions}
                                  minHeight="80px"
                                  isRep={isAccountRep(userSettings.accountType)}
                                  saveStatus={saveStatus}
                                  repCode={userSettings.repCode}
                                  onGroupCollapsedChanged={onGroupCollapsedChanged}
                                  clearInternalFilters={clearInternalFilters}
                                  displayRowMenu={isOneRowSelected}
                                  displayMenu={displayMenu}
                                  toggleMultiSort={toggleMultiSort}
                                  setCollapseLoading={setCollapseLoading}
                                />
                              </Col>

                            </div>
                          </>
                        )
                      }}
                    </ActionsHeader>
                    <div>
                      <GridFlask
                        collection={collection}
                        columnsMapping={columnsMapping}
                        id={moduleName}
                        grid={grid}
                        frozenColumns={frozenColumns}
                        hasAllColumnsHidden={hasAllColumnsHidden}
                        hasDataToDisplay={hasDataToDisplay}
                        onGridInitialized={onGridInitialized}
                        onInternalGridFiltersChanged={onInternalGridFiltersChanged}
                        onClearSelection={onClearSelection}
                        onRowSelected={onRowSelected}
                        onGroupPanelChanged={onGroupPanelChanged}
                        onCurrentItemChanged={onCurrentItemChanged}
                        onColSorted={onColSorted}
                        onColResized={onColResized}
                        onColDragged={onColDragged}
                        onColFreezed={onColFreezed}
                        onRowFreezed={onRowFreezed}
                        status={status}
                        formatters={formatters}
                        showFrozenBars
                        showGroupPanel
                        active={props.active}
                        groups={groups}
                        customAggregation={salesPercentAggregation}
                        playResetAnimation={playResetAnimation}
                        setFlexGridFilter={setFlexGridFilter}
                        menuRef={menuRef}
                        isMobile={isMobile}
                        currentItem={currentItem}
                        multiColumnSort={multiColumnSort}
                        exportProgress={exportProgress}
                      />
                    </div>
                  </React.Fragment>
                );
              }}
            </GridFlaskController>
          );
        }}
      </Query>
    </PageContainer>
  );
}

export default SalesAnalysis;
